
import React from 'react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { formatDistanceToNow } from 'date-fns';
import { Calendar, Clock, User, CreditCard, GraduationCap } from 'lucide-react';
import { Notification } from '@/contexts/notifications/types';
import { enhancedToast } from '@/utils/notifications/enhancedToast';

interface StudentNotificationsTabProps {
  notifications: Notification[];
  type: 'completion' | 'payment';
  onNotificationClick: (notification: Notification) => void;
}

export const StudentNotificationsTab: React.FC<StudentNotificationsTabProps> = ({
  notifications,
  type,
  onNotificationClick
}) => {
  const filteredNotifications = notifications.filter(n => n.type === type);
  
  if (filteredNotifications.length === 0) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        <p>No {type} notifications</p>
      </div>
    );
  }

  const handleNotificationClick = (notification: Notification) => {
    // Play sound based on notification type
    if (type === 'completion') {
      enhancedToast.success('Program Completion', notification.message);
    } else if (type === 'payment') {
      enhancedToast.warning('Payment Due', notification.message);
    }
    
    onNotificationClick(notification);
  };

  return (
    <ScrollArea className="h-48">
      <div className="space-y-2 p-2">
        {filteredNotifications.slice(0, 5).map((notification) => (
          <div
            key={notification.id}
            className={`p-3 rounded-lg border cursor-pointer hover:bg-muted/50 ${
              !notification.read ? 'border-primary/20 bg-primary/5' : 'border-border'
            }`}
            onClick={() => handleNotificationClick(notification)}
          >
            <div className="flex items-start gap-2">
              <div className="p-1 rounded-full bg-muted">
                {type === 'completion' ? (
                  <GraduationCap className="h-3 w-3 text-green-600" />
                ) : (
                  <CreditCard className="h-3 w-3 text-blue-600" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{notification.title}</p>
                <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                  {notification.message}
                </p>
                <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {formatDistanceToNow(new Date(notification.timestamp), { addSuffix: true })}
                  </span>
                  {notification.priority && (
                    <span className={`px-1.5 py-0.5 rounded text-xs ${
                      notification.priority === 'urgent' ? 'bg-red-100 text-red-700' :
                      notification.priority === 'important' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-blue-100 text-blue-700'
                    }`}>
                      {notification.priority}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
        
        {filteredNotifications.length > 5 && (
          <div className="text-center pt-2">
            <Button variant="ghost" size="sm" className="text-xs">
              View all {filteredNotifications.length} notifications
            </Button>
          </div>
        )}
      </div>
    </ScrollArea>
  );
};

interface TeacherNotificationsTabProps {
  notifications: any[];
  contentType: string;
  onNotificationClick: (notification: any) => void;
}

export const TeacherNotificationsTab: React.FC<TeacherNotificationsTabProps> = ({
  notifications,
  contentType,
  onNotificationClick
}) => {
  const filteredNotifications = notifications.filter(n => n.contentType === contentType);
  
  if (filteredNotifications.length === 0) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        <p>No {contentType.replace('-', ' ')} notifications</p>
      </div>
    );
  }

  const handleNotificationClick = (notification: any) => {
    // Play sound based on notification content type
    if (contentType === 'contract-expiration') {
      enhancedToast.warning('Contract Expiring', notification.message);
    } else if (contentType === 'salary-reminder') {
      enhancedToast.info('Salary Reminder', notification.message);
    }
    
    onNotificationClick(notification);
  };

  return (
    <ScrollArea className="h-48">
      <div className="space-y-2 p-2">
        {filteredNotifications.slice(0, 5).map((notification) => (
          <div
            key={notification.id}
            className={`p-3 rounded-lg border cursor-pointer hover:bg-muted/50 ${
              notification.isRead ? 'border-border' : 'border-primary/20 bg-primary/5'
            }`}
            onClick={() => handleNotificationClick(notification)}
          >
            <div className="flex items-start gap-2">
              <div className="p-1 rounded-full bg-muted">
                {contentType === 'contract-expiration' ? (
                  <Calendar className="h-3 w-3 text-yellow-600" />
                ) : (
                  <CreditCard className="h-3 w-3 text-blue-600" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{notification.title}</p>
                <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                  {notification.message}
                </p>
                <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <User className="h-3 w-3" />
                    {notification.teacherName}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {formatDistanceToNow(new Date(notification.date || notification.createdAt), { addSuffix: true })}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ))}
        
        {filteredNotifications.length > 5 && (
          <div className="text-center pt-2">
            <Button variant="ghost" size="sm" className="text-xs">
              View all {filteredNotifications.length} notifications
            </Button>
          </div>
        )}
      </div>
    </ScrollArea>
  );
};
